import { useEffect, useRef, useState } from "react";
import {
  Button,
  Field,
  IconButton,
  MenuButton,
  OptionChips,
  Segmented,
  Spinner,
  TextArea,
  TextInput,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type MailingBlock, type MailingBlockType, type MailingDesign } from "../../api/mailingApi";
import { BLOCK_PALETTE, newBlock } from "./mailingUi";

export const MERGE_TAGS = [
  { key: "first_name", label: "שם פרטי" },
  { key: "family_name", label: "שם משפחה" },
  { key: "full_name", label: "שם מלא" },
  { key: "display_name", label: "שם הלקוח / הארגון" },
  { key: "customer_code", label: "מספר לקוח" },
  { key: "email", label: "כתובת מייל" },
  { key: "org_name", label: "שם הארגון שלנו" },
];

/** "+ שם" menu that appends a merge tag to a text field */
export function MergeTagMenu({ onPick, disabled }: { onPick: (tag: string) => void; disabled?: boolean }) {
  return (
    <MenuButton
      align="end"
      width={220}
      items={MERGE_TAGS.map((t) => ({ key: t.key, label: t.label, hint: `{{${t.key}}}`, onClick: () => onPick(`{{${t.key}}}`) }))}
      renderButton={(p) => (
        <Button {...p} size="sm" variant="ghost" icon="user" disabled={disabled} type="button">
          שדה אישי
        </Button>
      )}
    />
  );
}

/**
 * Sandboxed preview of server-rendered email HTML — no scripts, no same-origin access.
 * The server is the only place that turns a design into HTML (ADR-0033).
 */
export function EmailPreview({ html, height = 560, title = "תצוגה מקדימה" }: { html: string | null; height?: number; title?: string }) {
  return (
    <iframe
      className="ml-preview__frame"
      title={title}
      sandbox=""
      srcDoc={html ?? ""}
      style={{ height }}
    />
  );
}

const ALIGN_OPTIONS = [
  { value: "right", label: "ימין" },
  { value: "center", label: "מרכז" },
  { value: "left", label: "שמאל" },
];

function BlockFields({ block, onChange, disabled }: { block: MailingBlock; onChange: (patch: Partial<MailingBlock>) => void; disabled?: boolean }) {
  const append = (key: "text" | "label", tag: string) => onChange({ [key]: `${block[key] ?? ""}${tag}` } as Partial<MailingBlock>);
  switch (block.type) {
    case "heading":
      return (
        <>
          <Field label="כותרת" full>
            <TextInput value={block.text ?? ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled} maxLength={300} />
          </Field>
          <div className="ml-row">
            <OptionChips ariaLabel="גודל" options={[{ value: "l", label: "גדולה" }, { value: "m", label: "בינונית" }, { value: "s", label: "קטנה" }]} value={block.size ?? "l"} onChange={(v) => onChange({ size: v as MailingBlock["size"] })} />
            <OptionChips ariaLabel="יישור" options={ALIGN_OPTIONS} value={block.align ?? "right"} onChange={(v) => onChange({ align: v as MailingBlock["align"] })} />
            <MergeTagMenu onPick={(t) => append("text", t)} disabled={disabled} />
          </div>
        </>
      );
    case "text":
      return (
        <>
          <Field label="טקסט" hint="**מודגש** · [טקסט הקישור](https://…) · קישורים רגילים הופכים ללחיצים" full>
            <TextArea rows={5} value={block.text ?? ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled} maxLength={20000} />
          </Field>
          <div className="ml-row">
            <OptionChips ariaLabel="יישור" options={ALIGN_OPTIONS} value={block.align ?? "right"} onChange={(v) => onChange({ align: v as MailingBlock["align"] })} />
            <MergeTagMenu onPick={(t) => append("text", t)} disabled={disabled} />
          </div>
        </>
      );
    case "button":
      return (
        <>
          <div className="ml-grid2">
            <Field label="טקסט הכפתור">
              <TextInput value={block.label ?? ""} onChange={(e) => onChange({ label: e.target.value })} disabled={disabled} maxLength={80} />
            </Field>
            <Field label="קישור" hint="https:// · mailto: · tel:">
              <TextInput ltr value={block.url ?? ""} onChange={(e) => onChange({ url: e.target.value.trim() })} disabled={disabled} maxLength={2000} />
            </Field>
          </div>
          <div className="ml-row">
            <OptionChips ariaLabel="יישור" options={ALIGN_OPTIONS} value={block.align ?? "center"} onChange={(v) => onChange({ align: v as MailingBlock["align"] })} />
            <label className="ml-color">
              צבע
              <input type="color" value={block.color || "#4f46e5"} onChange={(e) => onChange({ color: e.target.value })} disabled={disabled} />
            </label>
          </div>
        </>
      );
    case "image":
      return (
        <>
          <Field label="כתובת התמונה" hint="קישור https לתמונה שמאוחסנת באתר שלכם" full>
            <TextInput ltr value={block.src ?? ""} onChange={(e) => onChange({ src: e.target.value.trim() })} disabled={disabled} maxLength={2000} />
          </Field>
          <div className="ml-grid2">
            <Field label="תיאור (למי שהתמונות חסומות אצלו)">
              <TextInput value={block.alt ?? ""} onChange={(e) => onChange({ alt: e.target.value })} disabled={disabled} maxLength={200} />
            </Field>
            <Field label="קישור בלחיצה (רשות)">
              <TextInput ltr value={block.href ?? ""} onChange={(e) => onChange({ href: e.target.value.trim() })} disabled={disabled} maxLength={2000} />
            </Field>
          </div>
          <div className="ml-row">
            <label className="ml-range">
              רוחב {block.width ?? 100}%
              <input type="range" min={20} max={100} step={5} value={block.width ?? 100} onChange={(e) => onChange({ width: Number(e.target.value) })} disabled={disabled} />
            </label>
            <OptionChips ariaLabel="יישור" options={ALIGN_OPTIONS} value={block.align ?? "center"} onChange={(v) => onChange({ align: v as MailingBlock["align"] })} />
          </div>
        </>
      );
    case "spacer":
      return (
        <label className="ml-range">
          גובה {block.height ?? 24}px
          <input type="range" min={4} max={120} step={4} value={block.height ?? 24} onChange={(e) => onChange({ height: Number(e.target.value) })} disabled={disabled} />
        </label>
      );
    case "html":
      return (
        <Field label="HTML" hint="למתקדמים — מוכנס כמו שהוא. אפשר להשתמש ב־{{unsubscribe_url}} לקישור הסרה משלכם." full>
          <TextArea className="ml-code" dir="ltr" rows={8} value={block.html ?? ""} onChange={(e) => onChange({ html: e.target.value })} disabled={disabled} />
        </Field>
      );
    default:
      return <p className="ml-note">קו מפריד אופקי.</p>;
  }
}

function summary(block: MailingBlock): string {
  const t = (s: string | undefined) => (s ?? "").replace(/\s+/g, " ").trim().slice(0, 60);
  if (block.type === "heading" || block.type === "text") return t(block.text) || "(ריק)";
  if (block.type === "button") return t(block.label) || "(ללא טקסט)";
  if (block.type === "image") return block.src ? t(block.alt) || "תמונה" : "(אין תמונה)";
  if (block.type === "spacer") return `${block.height ?? 24}px`;
  if (block.type === "html") return t(block.html?.replace(/<[^>]+>/g, " ")) || "(ריק)";
  return "";
}

/**
 * Block email builder — headings, text, buttons, images, dividers, spacers and raw HTML,
 * with a live preview rendered by the server (the same renderer that sends).
 */
export function EmailBuilder({ design, onChange, disabled }: { design: MailingDesign; onChange: (d: MailingDesign) => void; disabled?: boolean }) {
  const [openId, setOpenId] = useState<string | null>(design.blocks[0]?.id ?? null);
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const [html, setHtml] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const id = ++seq.current;
    setRendering(true);
    const t = window.setTimeout(() => {
      mailingApi
        .render(design)
        .then((h) => {
          if (id === seq.current) setHtml(h);
        })
        .catch(() => undefined)
        .finally(() => {
          if (id === seq.current) setRendering(false);
        });
    }, 350);
    return () => window.clearTimeout(t);
  }, [design]);

  const blocks = design.blocks;
  const set = (next: MailingBlock[]) => onChange({ ...design, blocks: next });
  const patch = (id: string, p: Partial<MailingBlock>) => set(blocks.map((b) => (b.id === id ? { ...b, ...p } : b)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  const add = (type: MailingBlockType, at = blocks.length) => {
    const b = newBlock(type);
    const next = [...blocks];
    next.splice(at, 0, b);
    set(next);
    setOpenId(b.id);
  };
  const settings = design.settings ?? {};
  const setSetting = (key: keyof NonNullable<MailingDesign["settings"]>, value: string) => onChange({ ...design, settings: { ...settings, [key]: value } });

  return (
    <div className="ml-builder">
      <div className="ml-builder__edit">
        <div className="ml-row ml-row--between">
          <b>תוכן המייל</b>
          <div className="ml-row">
            <label className="ml-color" title="צבע מודגש (כפתורים וקישורים)">
              מודגש
              <input type="color" value={settings.accent || "#4f46e5"} onChange={(e) => setSetting("accent", e.target.value)} disabled={disabled} />
            </label>
            <label className="ml-color" title="צבע הרקע מסביב לתוכן">
              רקע
              <input type="color" value={settings.background || "#f3f4f6"} onChange={(e) => setSetting("background", e.target.value)} disabled={disabled} />
            </label>
          </div>
        </div>

        <ol className="ml-blocks">
          {blocks.map((b, i) => {
            const meta = BLOCK_PALETTE.find((p) => p.type === b.type);
            const open = openId === b.id;
            return (
              <li key={b.id} className={`ml-block${open ? " is-open" : ""}`}>
                <div className="ml-block__head">
                  <button type="button" className="ml-block__title" onClick={() => setOpenId(open ? null : b.id)} aria-expanded={open}>
                    <span className="ml-block__type">{meta?.label ?? b.type}</span>
                    <span className="ml-block__sum">{summary(b)}</span>
                  </button>
                  {!disabled ? (
                    <div className="ml-block__tools">
                      <IconButton icon="up" label="למעלה" size="sm" onClick={() => move(i, -1)} disabled={i === 0} />
                      <IconButton icon="down" label="למטה" size="sm" onClick={() => move(i, 1)} disabled={i === blocks.length - 1} />
                      <IconButton icon="copy" label="שכפול" size="sm" onClick={() => set([...blocks.slice(0, i + 1), { ...b, id: newBlock(b.type).id }, ...blocks.slice(i + 1)])} />
                      <IconButton icon="trash" label="מחיקה" size="sm" onClick={() => set(blocks.filter((x) => x.id !== b.id))} />
                    </div>
                  ) : null}
                </div>
                {open ? (
                  <div className="ml-block__body">
                    <BlockFields block={b} onChange={(p) => patch(b.id, p)} disabled={disabled} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>

        {!disabled ? (
          <div className="ml-palette" role="group" aria-label="הוספת רכיב">
            {BLOCK_PALETTE.map((p) => (
              <Button key={p.type} size="sm" icon={p.icon} onClick={() => add(p.type)} type="button">
                {p.label}
              </Button>
            ))}
          </div>
        ) : null}
        <p className="ml-note">
          קישור הסרה וכתובת הארגון מתווספים אוטומטית בתחתית כל מייל. אפשר לשים קישור הסרה משלכם עם כפתור שהקישור שלו {"{{unsubscribe_url}}"}.
        </p>
      </div>

      <div className="ml-builder__preview">
        <div className="ml-row ml-row--between">
          <Segmented
            ariaLabel="תצוגה"
            value={view}
            onChange={(v) => setView(v as "desktop")}
            items={[
              { value: "desktop", label: "מחשב" },
              { value: "mobile", label: "נייד" },
            ]}
          />
          {rendering ? <Spinner size={16} /> : null}
        </div>
        <div className={`ml-preview ml-preview--${view}`}>
          <EmailPreview html={html} />
        </div>
      </div>
    </div>
  );
}
